import React from 'react';
import { Bug, MapPin, MessageCircle, Flame, Search, X } from 'lucide-react';
import { MessageThread } from './types';
import { getCountriesFromFirebase } from '../../lib/firebase-service';
import { budgetCountries } from '../../data/budget-countries';
import { collection, query, where, getDocs, getCountFromServer, Timestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { getAllThreadVisits, hasNewMessagesSinceLastVisit } from '../../lib/thread-visits-service';
import { useAuth } from '../../lib/auth-context';

const playClickSound = () => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 800;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.1);
  } catch {}
};

const COUNTRY_DATA: Record<string, { flag: string; image: string }> = {};
budgetCountries.forEach(c => {
  COUNTRY_DATA[c.code] = { flag: c.flag, image: c.image };
});

interface ThreadSelectorProps {
  activeThread: MessageThread;
  onThreadChange: (thread: MessageThread) => void;
  countryCode?: string;
  refreshKey?: number;
  soundEnabled?: boolean;
}

export function ThreadSelector({ activeThread, onThreadChange, countryCode, refreshKey, soundEnabled = true }: ThreadSelectorProps) {
  const { user } = useAuth();
  const [countries, setCountries] = React.useState<{code: string; name: string; flag: string}[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [messageCounts, setMessageCounts] = React.useState<Record<string, number>>({});
  const [hasNewMessages, setHasNewMessages] = React.useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = React.useState('');

  const handleThreadChange = (thread: MessageThread) => {
    if (soundEnabled) playClickSound();
    onThreadChange(thread);
  };

  React.useEffect(() => {
    const loadCountries = async () => {
      try {
        const countriesData = await getCountriesFromFirebase();
        const sortedCountries = countriesData
          .map(c => ({ code: c.code, name: c.name, flag: c.flag }))
          .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
        setCountries(sortedCountries);
      } catch (error) {
        console.error('Error loading countries:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCountries();
  }, []);

  React.useEffect(() => {
    const fetchMessageCounts = async () => {
      if (!user?.uid) return;

      try {
        const counts: Record<string, number> = {};
        const newMessages: Record<string, boolean> = {};

        const threadVisits = await getAllThreadVisits(user.uid);

        const generalQuery = query(
          collection(db, 'messages'),
          where('thread_code', '==', 'general_discussion'),
          where('status', '==', 'active')
        );
        const generalSnapshot = await getCountFromServer(generalQuery);
        counts['general_discussion'] = generalSnapshot.data().count;

        const generalLastVisit = threadVisits['general_discussion'];
        newMessages['general_discussion'] = await hasNewMessagesSinceLastVisit(
          'general_discussion',
          generalLastVisit || null,
          db
        );

        const bugQuery = query(
          collection(db, 'messages'),
          where('thread_code', '==', 'bug_report'),
          where('status', '==', 'active')
        );
        const bugSnapshot = await getCountFromServer(bugQuery);
        counts['bug_report'] = bugSnapshot.data().count;

        const bugLastVisit = threadVisits['bug_report'];
        newMessages['bug_report'] = await hasNewMessagesSinceLastVisit(
          'bug_report',
          bugLastVisit || null,
          db
        );

        if (countries.length > 0) {
          const countryQueries = countries.map(async (country) => {
            const countryQuery = query(
              collection(db, 'messages'),
              where('thread_code', '==', country.code),
              where('status', '==', 'active')
            );
            const countrySnapshot = await getCountFromServer(countryQuery);

            const countryLastVisit = threadVisits[country.code];
            const hasNew = await hasNewMessagesSinceLastVisit(
              country.code,
              countryLastVisit || null,
              db
            );

            return {
              code: country.code,
              count: countrySnapshot.data().count,
              hasNew
            };
          });

          const countryResults = await Promise.all(countryQueries);
          countryResults.forEach(result => {
            counts[result.code] = result.count;
            newMessages[result.code] = result.hasNew;
          });
        }

        setMessageCounts(counts);
        setHasNewMessages(newMessages);
      } catch (error) {
        console.error('Error fetching message counts:', error);
      }
    };

    fetchMessageCounts();
  }, [countries, user?.uid, refreshKey]);

  const popularCountries = React.useMemo(() => {
    if (countries.length === 0) return [];
    return [...countries]
      .filter(c => (messageCounts[c.code] || 0) > 0)
      .sort((a, b) => (messageCounts[b.code] || 0) - (messageCounts[a.code] || 0))
      .slice(0, 3);
  }, [countries, messageCounts]);

  const filteredCountries = React.useMemo(() => {
    if (!searchQuery.trim()) return countries;
    const q = searchQuery.toLowerCase();
    return countries.filter(c => c.name.toLowerCase().includes(q));
  }, [countries, searchQuery]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <GeneralThreadButton
          icon={MessageCircle}
          label="Discussion generale"
          accentColor="blue"
          isActive={activeThread.code === 'general_discussion'}
          count={messageCounts['general_discussion'] || 0}
          isNew={hasNewMessages['general_discussion']}
          onClick={() => handleThreadChange({ code: 'general_discussion', type: 'general' })}
        />
        <GeneralThreadButton
          icon={Bug}
          label="Signaler un bug"
          accentColor="orange"
          isActive={activeThread.code === 'bug_report'}
          count={messageCounts['bug_report'] || 0}
          isNew={hasNewMessages['bug_report']}
          onClick={() => handleThreadChange({ code: 'bug_report', type: 'bug' })}
        />
      </div>

      {popularCountries.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 px-1 mb-2">
            <Flame className="w-3.5 h-3.5 text-orange-500" />
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
              Actives
            </p>
          </div>
          <div className="space-y-1">
            {popularCountries.map((country) => {
              const extraData = COUNTRY_DATA[country.code];
              const isActive = activeThread.code === country.code;
              const count = messageCounts[country.code] || 0;
              const isNew = hasNewMessages[country.code];
              const flag = country.flag || extraData?.flag;
              return (
                <button
                  key={`pop-${country.code}`}
                  onClick={() => handleThreadChange({ code: country.code, type: 'country' })}
                  className={`w-full relative overflow-hidden rounded-xl text-left transition-all duration-200 group ${
                    isActive
                      ? 'ring-2 ring-emerald-500/70 dark:ring-emerald-400/70 shadow-md shadow-emerald-500/10'
                      : 'hover:shadow-md hover:scale-[1.01]'
                  }`}
                >
                  <div className="relative h-14">
                    {extraData?.image && (
                      <img
                        src={extraData.image}
                        alt={country.name}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-transparent" />
                    <div className="relative h-full flex items-center gap-2.5 px-3">
                      {flag && (
                        <span className="text-lg drop-shadow-lg">{flag}</span>
                      )}
                      <div className="flex-1 min-w-0">
                        <span className="text-[13px] font-bold text-white drop-shadow-sm block truncate">
                          {country.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {isNew && (
                          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shadow-sm shadow-emerald-400/50" />
                        )}
                        {count > 0 && (
                          <span className="text-[10px] tabular-nums font-bold text-white/90 bg-white/20 backdrop-blur-sm px-1.5 py-0.5 rounded-md">
                            {count}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center gap-1.5 px-1 mb-2">
          <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
            Pays
          </p>
        </div>

        <div className="relative mb-2">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher..."
            className="w-full pl-8 pr-8 py-2 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 dark:text-white placeholder:text-gray-400 transition-shadow"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>

        <div className="space-y-px">
          {filteredCountries.map((country) => {
            const extraData = COUNTRY_DATA[country.code];
            const isActive = activeThread.code === country.code;
            const count = messageCounts[country.code] || 0;
            const isNew = hasNewMessages[country.code];
            const flag = country.flag || extraData?.flag;

            return (
              <button
                key={country.code}
                onClick={() => handleThreadChange({ code: country.code, type: 'country' })}
                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-all duration-150 ${
                  isActive
                    ? 'bg-emerald-50 dark:bg-emerald-950/30 ring-1 ring-emerald-200/60 dark:ring-emerald-800/40'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-800/60'
                }`}
              >
                {flag ? (
                  <span className="text-base flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-lg bg-gray-50 dark:bg-gray-800 transition-colors">
                    {flag}
                  </span>
                ) : (
                  <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500">
                    <MapPin className="w-3.5 h-3.5" />
                  </span>
                )}
                <span className={`text-[13px] truncate flex-1 ${
                  isActive
                    ? 'font-semibold text-emerald-900 dark:text-emerald-100'
                    : 'font-medium text-gray-600 dark:text-gray-400'
                }`}>
                  {country.name}
                </span>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {isNew && (
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  )}
                  {count > 0 && (
                    <span className={`text-[10px] tabular-nums font-semibold px-1.5 py-0.5 rounded-md ${
                      isActive
                        ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
                        : 'text-gray-400 dark:text-gray-500'
                    }`}>
                      {count}
                    </span>
                  )}
                </div>
              </button>
            );
          })}

          {filteredCountries.length === 0 && searchQuery && (
            <div className="py-6 text-center">
              <p className="text-xs text-gray-400 dark:text-gray-500">Aucun pays trouve</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function GeneralThreadButton({
  icon: Icon,
  label,
  accentColor,
  isActive,
  count,
  isNew,
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  accentColor: 'blue' | 'orange';
  isActive: boolean;
  count: number;
  isNew: boolean;
  onClick: () => void;
}) {
  const colors = {
    blue: {
      gradient: 'from-sky-500 to-blue-600',
      activeBg: 'bg-sky-50 dark:bg-sky-950/30 ring-1 ring-sky-200/60 dark:ring-sky-800/40',
      dot: 'bg-sky-500',
    },
    orange: {
      gradient: 'from-orange-500 to-rose-500',
      activeBg: 'bg-orange-50 dark:bg-orange-950/30 ring-1 ring-orange-200/60 dark:ring-orange-800/40',
      dot: 'bg-orange-500',
    },
  };

  const c = colors[accentColor];

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-200 ${
        isActive ? c.activeBg : 'hover:bg-gray-100 dark:hover:bg-gray-800/60'
      }`}
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${c.gradient} shadow-sm`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <span className={`text-[13px] block truncate ${
          isActive
            ? 'font-bold text-gray-900 dark:text-white'
            : 'font-medium text-gray-600 dark:text-gray-400'
        }`}>
          {label}
        </span>
        {count > 0 && (
          <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">
            {count} message{count > 1 ? 's' : ''}
          </span>
        )}
      </div>
      {isNew && (
        <span className={`w-2 h-2 rounded-full ${c.dot} animate-pulse flex-shrink-0`} />
      )}
    </button>
  );
}
